/* ============================================================================
   bundles.js — <bundles-component>, die Bundle-Auswahl der Produktseite und
   des Konfigurators
   ----------------------------------------------------------------------------
   Stand bis M6 als {% javascript %} in snippets/product-bundles.liquid und kam
   ueber compiled_assets/snippet-scripts.js auf jede Seite des Themes. Jetzt
   laedt das Snippet dieses Modul selbst, nur wo Bundles gerendert werden (R5),
   und davor assets/money.js (Summen und Rabatte) — Module laufen in
   Dokumentreihenfolge, `window.NKO.formatMoney` steht also beim Start bereit.

   VERTRAG
     nko:variant:change   (document) Hauptvariante gewechselt: Vorschaubild,
                          Verfuegbarkeit und Grundpreis der Karten nachziehen
     submit (document, Capture)  Kaufwache: unbestaetigtes Bundle -> Popup;
                          doppeltes Bundle im Warenkorb -> Warnung
                          (.double-bundle-popup-warn, <dialog>)
     customizable:variant-updated / :srcs-updated / :added-to-cart
                          Konfigurator (assets/configurator.js) -> Karten
     window.CustomizableATC()   Kauf im Konfigurator (Popup-Kaufknopf)
     theme.popupApp.open('bundle--' + id)   assets/popup.js
     validateCustomizableSubmit(), getSerializedForm()   ruft configurator.js
     .minicart            Titel und Preis der gewaehlten Karte in die Kaufleiste
     [data-variant-title] an <lens-category-widget> in den Popups

   AUF- UND ABBAU: alle Hoerer haengen an EINEM AbortController
   (`this.hoerer`), `disconnectedCallback` bricht ihn ab.
   ==========================================================================  */

class ProductBundles extends HTMLElement {
  connectedCallback() {
    this.hoerer?.abort();
    this.hoerer = new AbortController();
    const { signal } = this.hoerer;

    this.productSection = this.closest('.product-section');
    this.customizableSection = document.querySelector('.customizable-product-section');

    this.allSelects = this.querySelectorAll('.bundle__part-select');
    this.onPartChange = this.handlePartChange.bind(this);

    this.allRadios = this.querySelectorAll('.bundles__bundle-radio, #MainProductBundle');
    this.onMainRadioChange = this.handleMainRadioChange.bind(this);

    /* pdp-rebuild: das Produktformular wird hier NICHT festgehalten. Es liegt
       in der Region "buy" (blocks/product-form.liquid), die
       assets/product.js bei jedem Variantenwechsel ersetzt — ein
       gemerktes Formular und ein daran gebundener submit-Hoerer waeren danach
       weg. Die Sperre haengt deshalb in der Capture-Phase am Dokument
       (handleMainFormSubmit). */

    this.saveButtons = this.querySelectorAll('[data-save-bundle-id]');
    this.onSaveClick = this.handleSaveClick.bind(this);

    this.onMainVariantChange = this.handleMainVariantChange.bind(this);
    this.onCustomizableChange = this.handleCustomizableChange.bind(this);
    this.onCustomizableAddedToCart = this.handleCustomizableAddedToCart.bind(this);
    this.onMainFormSubmit = this.handleMainFormSubmit.bind(this);

    this.customizableAtcButtons = this.querySelectorAll('.js-bundle-customizable-atc');
    this.onCustomizableAtcClick = this.handleCustomizableAtcClick.bind(this);

    this.existingLineItemsEl = this.querySelector('#ExistingLineItems');

    for (const radio of this.allRadios) {
      radio.addEventListener('change', this.onMainRadioChange, { signal });
    }

    for (const select of this.allSelects) {
      select.addEventListener('change', this.onPartChange, { signal });
    }

    /* Preise und Vorschaubilder erst, wenn die Preisformatierung da ist.
       snippets/product-bundles.liquid laedt assets/money.js VOR diesem Modul,
       sie steht also beim Start bereit. Kam money.js nicht an, bleiben die
       Karten beim Liquid-Stand, statt dass updateBundleInfo beim ersten
       Select wirft und alles danach ungebunden bleibt.

       Bis M6 lief dieser Code als Snippet-Skript im Kopf VOR nko-money.js und
       wartete deshalb auf DOMContentLoaded. Jetzt laeuft er gleich beim
       Einhaengen; das Ergebnis ist dasselbe, nur frueher. */
    if (window.NKO?.formatMoney) {
      for (const select of this.allSelects) {
        this.syncBundlePartThumbnail(select);
        this.updateBundleInfo(select.dataset.bundleId);
      }
    }

    for (const button of this.saveButtons) {
      button.addEventListener('click', this.onSaveClick, { signal });
    }

    /* pdp-rebuild: `nko:variant:change` statt jQuery `variantChange` — jQuery
       laedt auf keiner Seite des Umbaus. Das Ereignis blubbert von
       <nko-product-form> hoch, die Variante steckt in `detail.variant`. */
    document.addEventListener('nko:variant:change', this.onMainVariantChange, { signal });
    this.customizableSection?.addEventListener('customizable:variant-updated', this.onCustomizableChange, { signal });
    this.customizableSection?.addEventListener('customizable:srcs-updated', this.onCustomizableChange, { signal });
    this.customizableSection?.addEventListener('customizable:added-to-cart', this.onCustomizableAddedToCart, {
      signal,
    });

    for (const button of this.customizableAtcButtons) {
      button.addEventListener('click', this.onCustomizableAtcClick, { signal });
    }

    if (!this.dataset.isCustomizable) {
      document.addEventListener('submit', this.onMainFormSubmit, { capture: true, signal });
    }
  }

  disconnectedCallback() {
    this.hoerer?.abort();
    this.hoerer = null;
  }

  handleMainRadioChange(e) {
    const el = e.currentTarget;
    const allFormInputs = this.querySelectorAll(
      ".bundles__bundle-section [type='hidden'], .bundles__bundle-section select"
    );

    for (const input of allFormInputs) {
      input.disabled = 'disabled';
    }
    if (el.checked && el.id !== 'MainProductBundle') {
      const inputs = [...el.closest('.bundles__bundle-section').querySelectorAll("[type='hidden'], select")];
      for (const input of inputs) {
        if (input.dataset.disabled) {
          continue;
        }
        input.removeAttribute('disabled');
      }
    }

    if (el.checked) this.syncStickyBar(el.closest('.group'));
  }

  /* pdp-rebuild: Titel und Preis der gewaehlten Karte in die Sticky-Kaufleiste.
     Stand im Legacy in app.js `checkPrices` (:1349-1373 auf main), das der
     Umbau nicht mehr laedt; der Umbau hatte es in dieses Snippet geholt. Der
     Freelancer-Umbau (25bfc50) schrieb nur noch den Preis, und nur bei
     Teile- und Variantenwechsel, nicht beim Wechsel der Karte.

     Zwei freigegebene Korrekturen aus dem Umbau bleiben:
       [A21] `.price--old` der Leiste wird mit `hide` geschaltet — vorher
             stand der Streichpreis in einem versteckten Element.
       [A22] Zurueck auf das Originalprodukt: der Name kommt aus dem
             Variantenobjekt (`script[data-nko-variant]`), nicht aus dem
             Optionstext der Selectbox (der den Preis mit enthaelt).
     Karten ohne Rabatt haben keinen `.price--old` (updateBundleInfo) — dann
     wird die Leiste geleert, statt den Streichpreis der vorigen Karte stehen
     zu lassen.

     Im Konfigurator schreibt assets/configurator.js Titel und Preis selbst
     (`updateMinicart`); dort nur der Preis, wie im Freelancer-Umbau. */
  syncStickyBar(gruppe) {
    const leiste = document.querySelector('.minicart');
    if (!gruppe || !leiste) return;

    const preis = gruppe.querySelector('.price--default');
    const preisZiel = leiste.querySelector('.minicart__prices .price--default');
    if (preis && preisZiel) preisZiel.innerHTML = preis.innerHTML;

    if (this.dataset.isCustomizable) return;

    const titelZiel = leiste.querySelector('.minicart__variant');
    if (titelZiel) {
      if (gruppe.classList.contains('group--orig')) {
        // [A22]
        let name = '';
        try {
          name = JSON.parse(document.querySelector('script[data-nko-variant]').textContent).title || '';
        } catch (err) {
          name = document.querySelector('.js-main-product-selectbox option:checked')?.textContent ?? '';
        }
        titelZiel.textContent = name;
      } else {
        const titel = gruppe.querySelector('.group__title');
        if (titel) titelZiel.innerHTML = titel.innerHTML;
      }
    }

    const altZiel = leiste.querySelector('.price--old');
    if (altZiel) {
      altZiel.innerHTML = gruppe.querySelector('.price--old')?.innerHTML ?? '';
      // [A21]
      altZiel.classList.toggle('hide', !altZiel.textContent.trim());
    }
  }

  /* pdp-rebuild: "Auswahl bestaetigen" und "In den Warenkorb" im Bundle-Popup.
     Lag im Legacy in app.js (:906-915 auf main), das der Umbau nicht laedt.
     Ohne den Schalter blieb die Karte `is-not-selected`, und die Sperre in
     handleMainFormSubmit bzw. validateCustomizableSubmit blockierte JEDEN
     Kauf und oeffnete das Popup erneut. Laeuft vor dem submit des
     Popup-Knopfes (Klick-Hoerer vor der Standardaktion). */
  handleSaveClick(e) {
    const bundleId = e.currentTarget.dataset.saveBundleId;
    const group = this.querySelector(
      `.bundles__bundle-section[data-bundle-id="${CSS.escape(bundleId)}"] .group--bundle`
    );
    group?.classList.remove('is-not-selected');
    group?.classList.add('is-selected');
  }

  handlePartChange(e) {
    const select = e.target;
    this.syncBundlePartThumbnail(select);
    this.updateBundleInfo(select.dataset.bundleId);
  }

  syncBundlePartThumbnail(select) {
    const section = select.closest('.bundles__bundle-section');
    const selectedOption = select.options[select.selectedIndex];
    const mediaId = selectedOption?.dataset.mediaId;
    const thumbnailIndex = Number(select.dataset.index) - 1;
    const thumbnails = section?.querySelectorAll(
      '.group__thumbnails .group__thumbnail:not([data-bundle-main-product-thumbnail])'
    );
    const thumbnail = thumbnails?.[thumbnailIndex];

    if (!section || !thumbnail || !mediaId) return;

    const selectedImage = select.closest('li')?.querySelector(`.bundle__image[data-media-id="${mediaId}"] img`);
    const thumbnailImage = thumbnail.querySelector('img');

    if (!selectedImage || !thumbnailImage) return;

    thumbnail.dataset.mediaId = mediaId;
    thumbnailImage.src = this.getSizedBundleImageUrl(selectedImage.currentSrc || selectedImage.src, 40);
    thumbnailImage.removeAttribute('srcset');
    thumbnailImage.alt = selectedImage.alt || '';
  }

  getSizedBundleImageUrl(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (e) {
      return src;
    }
  }

  updateAllBundleInfo(basePrice) {
    if (basePrice === undefined || basePrice === null) return;
    const sections = document.querySelectorAll('.bc-bundle-section[data-bundle-id]');
    for (const section of sections) {
      this.updateBundleInfo(section.dataset.bundleId, basePrice);
    }
  }

  updateBundleInfo(bundleId, basePriceOverride) {
    const section = document.querySelector(`.bc-bundle-section[data-bundle-id="${bundleId}"]`);

    let totalPrice = 0;
    const basePrice =
      basePriceOverride === undefined ? document.querySelector('.js-price-default').dataset.price : basePriceOverride;
    totalPrice += Number(basePrice);

    const selects = section.querySelectorAll('select');
    for (const select of selects) {
      const selectedOption = select.options[select.selectedIndex];
      const selectedImageId = selectedOption.dataset.mediaId;
      const selectedVariantTitle = selectedOption.dataset.title;

      // Update images
      const images = select.closest('li').querySelectorAll('.bundle__image');
      for (const image of images) {
        if (image.dataset.mediaId === selectedImageId) {
          image.classList.remove('hide');
        } else {
          image.classList.add('hide');
        }
      }

      // Update lens category
      const lensCategoryEl = select.closest('li').querySelector('lens-category-widget');
      if (lensCategoryEl) {
        lensCategoryEl.dataset.variantTitle = selectedVariantTitle;
      }

      const selectedPrice = Number(selectedOption.dataset.price);
      if (!Number.isNaN(selectedPrice)) {
        totalPrice += selectedPrice;
      }
    }

    // Update price
    const priceEl = section.querySelector('.group__prices');
    const discount = Number(section.dataset.bundleDiscount) || 0;
    const priceOverrideMoney = priceEl.dataset.priceOverrideMoney;

    let newMoney = window.NKO.formatMoney(totalPrice);
    let oldMoney = '';
    if (priceOverrideMoney) {
      oldMoney = newMoney;
      newMoney = priceOverrideMoney;
    } else if (discount) {
      oldMoney = newMoney;
      newMoney = window.NKO.formatMoney(totalPrice - discount * 100);
    }

    // Die Leerzeichen im Template sind die des frueheren Snippet-Skripts —
    // das eingefuegte Markup bleibt so zeichengleich.
    priceEl.innerHTML = `
          ${oldMoney ? `<span class="price price--old">${oldMoney}</span>` : ''}
          <span class="price price--default">${newMoney}</span>
        `;

    if (section.querySelector('.bundles__bundle-radio')?.checked) {
      this.syncStickyBar(section.querySelector('.group--bundle'));
    }
  }

  handleMainVariantChange(e) {
    if (this.dataset.isCustomizable) return;
    const variant = e.detail?.variant;
    // Update main product thumbnail images
    const imageUrl = variant?.featured_media?.preview_image?.src;
    if (imageUrl) {
      const newUrl = this.getSizedBundleImageUrl(imageUrl, 40);
      const thumbnails = document.querySelectorAll('.group__thumbnail[data-bundle-main-product-thumbnail]');
      for (const thumbnail of thumbnails) {
        const img = thumbnail.querySelector('img');
        img.src = newUrl;
      }
    }

    // Update availability status
    const status = variant?.available ? 'available' : 'unavailable';
    const elements = document.querySelectorAll('.product__combo--bundle [data-availability-status]');
    for (const el of elements) {
      el.dataset.availabilityStatus = status;
    }

    /* pdp-rebuild: auch den Preis der Hauptkarte nachziehen. Sonst rechnete
       der naechste Teilewechsel wieder mit `data-price` der ersten Variante
       (updateBundleInfo liest ihn), und die Karte zeigte weiter deren Preis. */
    const basePriceEl = document.querySelector('.product__combo--bundle .js-price-default');
    if (basePriceEl && variant && typeof variant.price === 'number' && window.NKO?.formatMoney) {
      basePriceEl.dataset.price = variant.price;
      basePriceEl.textContent = window.NKO.formatMoney(variant.price);
    }

    this.updateAllBundleInfo(variant?.price);
  }

  handleCustomizableChange(e) {
    if (!e.detail) return;
    const { available, totalPrice, formattedPrice, srcs } = e.detail;

    if (srcs) {
      // Customizable products have up to 3 layers
      const thumbnails = document.querySelectorAll('.group__thumbnail[data-bundle-main-product-thumbnail]');
      for (const thumbnail of thumbnails) {
        const images = thumbnail.querySelectorAll('img');
        [...images].forEach((img) => (img.hidden = true));
        for (let i = 0; i < srcs.length; i++) {
          const img = images?.[i];
          // This should not be possible
          if (!img || !srcs[i]) continue;
          img.src = srcs[i];
          img.hidden = false;
        }
      }
    }

    if ([available, totalPrice, formattedPrice].some((v) => v !== undefined)) {
      const availabilityElements = document.querySelectorAll(
        '.product__combo--bundle [data-customizable-availability-status]'
      );
      for (const el of availabilityElements) {
        el.dataset.customizableAvailabilityStatus = available ? 'available' : 'unavailable';
      }

      const priceEl = document.querySelector('.product__combo--bundle .js-price-default');
      if (priceEl) {
        priceEl.dataset.price = totalPrice;
        priceEl.textContent = formattedPrice;
      }

      this.updateAllBundleInfo(totalPrice);
    }
  }

  async handleMainFormSubmit(e) {
    // Dokumentweit in der Capture-Phase (siehe connectedCallback): nur das
    // eigene Produktformular. `form.id` geht nicht — das liefert das
    // <select name="id"> (docs/pdp-contract.md).
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || form.getAttribute('id') !== this.dataset.formId) return;

    // Check if there are unsaved bundles
    const unselectedBundle = document.querySelector('.group--bundle.is-not-selected .group__value:checked');
    if (unselectedBundle) {
      e.preventDefault();
      e.stopPropagation();
      theme.popupApp.open('bundle--' + unselectedBundle.dataset.popupNum);
      return;
    }

    // Validate double purchasing
    const bundleRadio = document.querySelector('.bundles__bundle-radio:checked');
    if (!bundleRadio) {
      return;
    }
    const bundleId = bundleRadio.dataset.bundleId;
    const bundleSection = bundleRadio.closest('.bundles__bundle-section');
    const mainVariantId = form.querySelector("[name='id']")?.value;
    /* Ledger C02: flach, nicht `[[...selects], mainVariantId]` — im
       verschachtelten Array fand `.includes(id)` nie eine einzelne ID, die
       Duplikatspruefung schlug falsch an (Testfall 7.1). */
    const variantIds = [...[...bundleSection.querySelectorAll('select')].map((s) => s.value), mainVariantId].filter(
      Boolean
    );

    if (this.hasCartBundleDuplicates(bundleId, variantIds)) {
      const warnPopup = document.querySelector('.double-bundle-popup-warn');
      if (warnPopup && !warnPopup.dataset?.dismissed && typeof warnPopup.showModal === 'function') {
        e.preventDefault();
        e.stopPropagation();
        try {
          await this.userDoubleBundleConfirmation(warnPopup);
          warnPopup.dataset.dismissed = 'true';
          // Waehrend das Popup offen war, kann ein Variantenwechsel das
          // Formular ersetzt haben (Region "buy"). Ein abgehaengtes Formular
          // schickt nichts ab — dann das aktuelle mit derselben id nehmen.
          (form.isConnected ? form : document.getElementById(this.dataset.formId))?.requestSubmit();
        } catch (e) {
          return;
        }
      }
    }

    setTimeout(() => {
      this.updateLineItemsJson();
    }, 500);

    this.incrementBundleNum();
  }

  handleCustomizableAddedToCart() {
    this.updateLineItemsJson();
    this.incrementBundleNum();
  }

  handleCustomizableAtcClick(e) {
    const bundleId = e.currentTarget.dataset.saveBundleId;
    const group = this.querySelector(`.bundles__bundle-section[data-bundle-id="${bundleId}"] .group--bundle`);
    group?.classList.remove('is-not-selected');
    group?.classList.add('is-selected');

    window.CustomizableATC?.();
  }

  async validateCustomizableSubmit() {
    // Check if there are unsaved bundles
    const unselectedBundle = document.querySelector('.group--bundle.is-not-selected .group__value:checked');
    if (unselectedBundle) {
      theme.popupApp.open('bundle--' + unselectedBundle.dataset.popupNum);
      return false;
    }

    // Validate double purchasing is skipped due
    // to complexity
    return true;
  }

  async updateLineItemsJson() {
    try {
      const script = this.existingLineItemsEl;
      const cartRes = await fetch(`/cart.js`);
      const cart = await cartRes.json();
      const bundleItems = cart.items.map((it) => ({ properties: it.properties, variant_id: it.variant_id }));
      script.innerHTML = JSON.stringify(bundleItems);
    } catch (e) {
      /* Warenkorb nicht lesbar: die Doppelkauf-Pruefung rechnet dann mit dem
         Stand beim Seitenaufruf weiter. */
    }
  }

  incrementBundleNum() {
    const inputs = document.querySelectorAll("input[name$='properties[_bundleNum]']");
    for (const input of inputs) {
      input.value = String(Number(input.value || 1) + 1);
    }
  }

  // Ledger C01: `return` nach der Schleife, nicht darin (erledigt in 25bfc50).
  // Testfall 7.1 (gleiches Bundle zweimal, gleiche Varianten) scheiterte an
  // C02 in handleMainFormSubmit — dort jetzt flach.
  hasCartBundleDuplicates(bundleId, selectedVariantIds) {
    try {
      const items = JSON.parse(this.existingLineItemsEl.innerHTML || '{}');
      const existingBundles = {};
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!item.properties._bundleId) continue;
        if (item.properties._bundleId !== bundleId) continue;

        if (!existingBundles[item.properties._bundleNum]) {
          existingBundles[item.properties._bundleNum] = [];
        }
        existingBundles[item.properties._bundleNum].push(String(item.variant_id));
      }
      const allBundleNums = Object.keys(existingBundles);
      for (let i = 0; i < allBundleNums.length; i++) {
        const variantIds = existingBundles[allBundleNums[i]];
        const hasOneCommon = variantIds.some(function (id) {
          return selectedVariantIds.includes(id);
        });
        const hasOneDifferent = variantIds.some(function (id) {
          return !selectedVariantIds.includes(id);
        });
        if (hasOneCommon && hasOneDifferent) {
          return true;
        }
      }
      return false;
    } catch (error) {
      // Unlesbares JSON: lieber kaufen lassen als grundlos warnen.
      return false;
    }
  }

  getSerializedForm() {
    const properties = [...this.querySelectorAll("input[name^='properties']")].reduce((acc, input) => {
      if (input.disabled) return acc;
      const nameMatch = input.name.match(/properties\[(.+)\]/);
      if (nameMatch) {
        acc[nameMatch[1]] = input.value;
      }
      return acc;
    }, {});

    const itemsByName = {};
    const itemFields = [...this.querySelectorAll("input[name^='items'], select[name^='items']")];

    for (const field of itemFields) {
      if (field.disabled) continue;
      const itemNameMatch = field.name.match(/items\[(.+?)\]/);
      const itemName = itemNameMatch ? itemNameMatch[1] : null;
      if (!itemsByName[itemName]) {
        itemsByName[itemName] = { properties: {} };
      }

      const propertyNameMatch = field.name.match(/items\[(.+)\]properties\[(.+)\]/);
      if (propertyNameMatch) {
        const propertyName = propertyNameMatch[2];
        const propertyValue = field.value;
        itemsByName[itemName].properties[propertyName] = propertyValue;
      } else {
        const itemKey = field.name.replace(`items[${itemName}]`, '');
        itemsByName[itemName][itemKey] = itemKey === 'quantity' ? Number(field.value) : field.value;
      }
    }

    const items = Object.values(itemsByName);
    return { properties, items };
  }

  /* Oeffnet die Warnung modal und wartet auf die Antwort. Vorher haengte jeder
     Aufruf drei neue Hoerer an und entfernte keinen — nach der dritten
     Warnung lief jeder Klick dreifach. Jetzt raeumt jeder Ausgang alle ab.
     Abbrechen: Knopf, Escape (`cancel`) oder Klick auf den Hintergrund —
     der trifft das <dialog> selbst, der Inhalt liegt in `__inner`. */
  userDoubleBundleConfirmation(dialog) {
    return new Promise(function (resolve, reject) {
      const abbrechenKnopf = dialog.querySelector('#double-bundle-popup-warn-cancel');
      const weiterKnopf = dialog.querySelector('#double-bundle-popup-warn-confirm');

      const ende = function (ok) {
        abbrechenKnopf.removeEventListener('click', abbrechen);
        weiterKnopf.removeEventListener('click', weiter);
        dialog.removeEventListener('click', hintergrund);
        dialog.removeEventListener('cancel', escape);
        if (dialog.open) dialog.close();
        if (ok) resolve();
        else reject(new Error('User cancelled'));
      };
      const abbrechen = function () { ende(false); };
      const weiter = function () { ende(true); };
      const hintergrund = function (e) { if (e.target === dialog) ende(false); };
      const escape = function (e) { e.preventDefault(); ende(false); };

      abbrechenKnopf.addEventListener('click', abbrechen);
      weiterKnopf.addEventListener('click', weiter);
      dialog.addEventListener('click', hintergrund);
      dialog.addEventListener('cancel', escape);
      dialog.showModal();
    });
  }
}

if (!customElements.get('bundles-component')) {
  customElements.define('bundles-component', ProductBundles);
}

/* ----------------------------------------------------------------------------
   NUR ALS MODUL LADEN: <script src="…" type="module">, nie mit `defer`.
   Module laufen wie `defer` nach dem Parsen in Dokumentreihenfolge, aber nur
   einmal je URL und im strict mode.
   ---------------------------------------------------------------------------- */
